import { useState } from "react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { COMPANY } from "@/data/site";
import { Phone, Mail, Globe, MapPin } from "lucide-react";

const INFO = [
  { icon: Phone, label: "Numero Verde", value: COMPANY.phone },
  { icon: Mail, label: "E-mail", value: COMPANY.email },
  { icon: Globe, label: "Website", value: COMPANY.web },
  { icon: MapPin, label: "Sede", value: COMPANY.address },
];

const inputCls =
  "w-full bg-[#0D0E13] border border-white/10 rounded-2xl px-5 py-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-orange-500/70 transition-colors duration-300";

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post("/contact", form);
      toast.success(res.data.message);
      setForm({ name: "", email: "", phone: "", city: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="py-24 sm:py-32 bg-[#0D0E13] border-t border-white/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <SectionHeading
            eyebrow="Contattaci"
            title={
              <>
                Il team AirDroneJet <span className="fire-text">è pronto a decollare</span>
              </>
            }
            sub="Richiedi un preventivo personalizzato o semplicemente raccontaci il tuo impianto: ti rispondiamo entro 24 ore."
          />
          <div className="grid sm:grid-cols-2 gap-4 mt-12">
            {INFO.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.06}>
                <div className="bg-[#13141B] border border-white/10 rounded-2xl p-5 flex items-center gap-4" data-testid={`contact-info-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <c.icon size={17} className="text-orange-500" />
                  </div>
                  <div>
                    <p className="font-mono-adj text-[9px] uppercase tracking-[0.25em] text-slate-500">{c.label}</p>
                    <p className="text-sm font-semibold text-white mt-1">{c.value}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="bg-[#13141B] border border-white/10 rounded-3xl p-7 sm:p-9 flex flex-col gap-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Nome e Cognome" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input placeholder="Telefono" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
              <input placeholder="Città" value={form.city} onChange={set("city")} className={inputCls} data-testid="contact-city-input" />
            </div>
            <textarea
              required
              rows={5}
              placeholder="Raccontaci il tuo impianto o la tua richiesta speciale..."
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} resize-none`}
              data-testid="contact-message-input"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="fire-bg text-white font-mono-adj text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:scale-[1.02] active:scale-95 transition-transform duration-300 disabled:opacity-50"
            >
              {sending ? "Invio in corso..." : "Invia Richiesta"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
